/**
 * Change history for the local CMS.
 *
 * Every mutation records an entry with enough information to undo it: the
 * before-state of whatever changed, plus the after-state for display. Reverting
 * restores the recorded before-state rather than replaying an inverse
 * operation, which keeps revert correct even for cascading deletes.
 *
 * Stored in localStorage alongside the KB. Capped, because article bodies are
 * large and localStorage is not.
 */

import { ulid } from "ulid"
import type { KBStore, AnyNode, NodeKind } from "./types"
import { pushHistory } from "../shared/persistence/historySync"

export type ChangeAction =
  | "create"
  | "update"
  | "delete"
  | "publish"
  | "labels"
  | "move"
  | "translate"
  | "revert"

export interface ChangeEntry {
  id: string
  at: number
  action: ChangeAction
  /** Whether the change came from the agent or a person editing directly. */
  actor: "agent" | "user"
  /**
   * Attribution. For agent changes this is the signed-in user who prompted it,
   * since the agent acts on their behalf — `actor` distinguishes the two.
   */
  authorName?: string
  authorEmail?: string
  authorId?: string
  /** Model id, when the agent made the change. */
  viaModel?: string
  nodeId: string
  nodeKind: NodeKind
  /** Human-readable title at the time of the change. */
  nodeTitle: string
  /** Breadcrumb path at the time of the change. */
  path: string
  locale?: string
  /** One-line description shown in the log. */
  summary: string

  /**
   * Snapshot needed to undo this entry.
   *
   * - create: nothing to restore; revert deletes the node.
   * - update/publish/labels/move/translate: the node as it was.
   * - delete: the node plus every descendant that was removed with it.
   */
  before?: {
    nodes: AnyNode[]
  }
  /** Post-change node, for diffing in the UI. Omitted for deletes. */
  after?: {
    nodes: AnyNode[]
  }
  /** True once this entry has been reverted, so the UI can grey it out. */
  reverted?: boolean
}

const HISTORY_KEY_PREFIX = "kb-cms-history"
const DEFAULT_KB = "default"
const MAX_ENTRIES = 200

/** kbId matches the id passed to store.ts's loadStore/saveStore, so a team's history stays with its KB. */
function historyKeyFor(kbId: string) { return kbId === DEFAULT_KB ? HISTORY_KEY_PREFIX : `${HISTORY_KEY_PREFIX}:${kbId}` }

/*
 * In-memory cache backing the synchronous history API.
 *
 * Entries are written to Postgres (see shared/persistence/historySync) and
 * read back on load; this map is what the synchronous readers see in between.
 * Not localStorage -- a change log that only the browser which made the
 * change can read is not an audit trail.
 */
const cache = new Map<string, ChangeEntry[]>()

export function loadHistory(kbId = DEFAULT_KB): ChangeEntry[] {
  return cache.get(historyKeyFor(kbId)) ?? []
}

export function saveHistory(entries: ChangeEntry[], kbId = DEFAULT_KB): void {
  pushHistory("articles", entries)
  cache.set(historyKeyFor(kbId), entries.slice(-MAX_ENTRIES))
}

export function clearHistory(kbId = DEFAULT_KB): void {
  cache.delete(historyKeyFor(kbId))
}

/** Collects a node and everything beneath it — the snapshot a delete needs. */
export function collectSubtree(store: KBStore, id: string): AnyNode[] {
  const out: AnyNode[] = []
  const cat = store.categories[id]
  const sec = store.sections[id]
  const art = store.articles[id]

  if (art) return [art]

  // A section reachable by two routes (or swept twice below) must only appear
  // once, or revert would re-insert duplicates.
  const seen = new Set<string>()

  const pushSectionTree = (sectionId: string) => {
    const s = store.sections[sectionId]
    if (!s || seen.has(sectionId)) return
    seen.add(sectionId)
    out.push(s)
    for (const a of Object.values(store.articles)) if (a.sectionId === sectionId) out.push(a)
    for (const child of Object.values(store.sections)) {
      if (child.parentSectionId === sectionId) pushSectionTree(child.id)
    }
  }

  if (cat) {
    out.push(cat)
    for (const s of Object.values(store.sections)) {
      if (s.categoryId === id && s.parentSectionId === null) pushSectionTree(s.id)
    }
    // Mirror deleteNode's defensive orphan sweep (see cms/store.ts): a section
    // whose parentSectionId points at a missing parent is still deleted with
    // the category, so it must be in the snapshot or revert cannot restore it.
    for (const s of Object.values(store.sections)) {
      if (s.categoryId === id) pushSectionTree(s.id)
    }
    return out
  }
  if (sec) {
    pushSectionTree(id)
    return out
  }
  return out
}

export function makeEntry(args: Omit<ChangeEntry, "id" | "at">): ChangeEntry {
  return { ...args, id: ulid(), at: Date.now() }
}

/**
 * Applies a revert: puts the recorded `before` nodes back, and removes nodes
 * that the change had created.
 *
 * Returns the new store plus a description of what happened, or an error string
 * when the entry can't be reverted.
 */
export function revertEntry(
  store: KBStore,
  entry: ChangeEntry
): { store: KBStore; description: string } | { error: string } {
  if (entry.reverted) return { error: "That change has already been reverted." }

  const next: KBStore = JSON.parse(JSON.stringify(store))

  const put = (node: AnyNode) => {
    if (node.kind === "category") next.categories[node.id] = node as any
    else if (node.kind === "section") next.sections[node.id] = node as any
    else next.articles[node.id] = node as any
  }
  const drop = (id: string) => {
    delete next.categories[id]
    delete next.sections[id]
    delete next.articles[id]
  }

  if (entry.action === "create") {
    // Undo a creation by removing it — plus anything added beneath it since,
    // otherwise those children would be orphaned and unreachable in the tree.
    for (const n of collectSubtree(next, entry.nodeId)) drop(n.id)
    drop(entry.nodeId)
    return { store: next, description: `Removed "${entry.nodeTitle}" (undo of create)` }
  }

  if (!entry.before?.nodes?.length) {
    return { error: "This entry has no saved snapshot, so it can't be reverted." }
  }

  if (entry.action === "delete") {
    for (const n of entry.before.nodes) put(n)
    return {
      store: next,
      description: `Restored "${entry.nodeTitle}" and ${entry.before.nodes.length - 1} nested item(s)`,
    }
  }

  // update / publish / labels / move / translate: restore the prior node state.
  for (const n of entry.before.nodes) put(n)
  return { store: next, description: `Reverted ${entry.action} on "${entry.nodeTitle}"` }
}

/**
 * The version timeline for one node: every recorded state of it, newest first.
 *
 * Distinct from revert. Revert undoes one specific change; this answers "what
 * did this article look like at each point, and put it back to that." Each
 * version is the `after` snapshot of a change (or the `before` snapshot of the
 * next one, for the state that preceded the earliest recorded change).
 */
export interface NodeVersion {
  /**
   * Identifies this VERSION, not the entry it came from — one entry can yield
   * two versions (its `after` state, and its `before` state when it is the
   * earliest recorded change), and those must be separately addressable or
   * the prior state can never be restored. The prior-state row is suffixed
   * `:before`; use `sourceEntryId` for the raw entry id.
   */
  entryId: string
  /** The history entry this version was derived from. */
  sourceEntryId: string
  at: number
  action: ChangeAction
  author: string
  actor: "agent" | "user"
  summary: string
  /** The node as it was at this point. Absent when the snapshot was trimmed. */
  node?: AnyNode
  /** True for the state BEFORE the entry, rather than after it. */
  isPriorState?: boolean
}

/** Version id for the state recorded BEFORE an entry, distinct from its after-state. */
export const priorVersionId = (entryId: string) => `${entryId}:before`

export function versionsForNode(entries: ChangeEntry[], nodeId: string): NodeVersion[] {
  // Exclude BOTH the synthetic "revert" log lines (which have no snapshot of
  // their own worth restoring) and entries that have since been reverted —
  // an undone change's snapshot is no longer a state this node was ever left
  // in, so offering to restore it would be misleading.
  const relevant = entries.filter(e => e.nodeId === nodeId && e.action !== "revert" && !e.reverted)
  const out: NodeVersion[] = []

  for (const e of relevant) {
    const author = e.authorName || e.authorEmail || (e.actor === "agent" ? "agent" : "unknown")
    const afterNode = e.after?.nodes.find(n => n.id === nodeId)
    if (afterNode) {
      out.push({
        entryId: e.id, sourceEntryId: e.id, at: e.at, action: e.action,
        author, actor: e.actor, summary: e.summary, node: afterNode,
      })
    }
  }

  // Prepend the state before the earliest change, so the original is restorable.
  // Its id must differ from that entry's after-state id, or lookups by id
  // resolve to the wrong version and the original becomes unreachable.
  const earliest = relevant[0]
  const beforeNode = earliest?.before?.nodes.find(n => n.id === nodeId)
  if (beforeNode) {
    const author = earliest.authorName || earliest.authorEmail || (earliest.actor === "agent" ? "agent" : "unknown")
    out.unshift({
      entryId: priorVersionId(earliest.id), sourceEntryId: earliest.id,
      at: earliest.at, action: earliest.action, author,
      actor: earliest.actor, summary: `State before: ${earliest.summary}`,
      node: beforeNode, isPriorState: true,
    })
  }

  return out.reverse() // newest first
}

/** Writes one recorded version of a node back into the store. */
export function restoreVersion(store: KBStore, node: AnyNode): KBStore {
  const next: KBStore = JSON.parse(JSON.stringify(store))
  if (node.kind === "category") next.categories[node.id] = node as any
  else if (node.kind === "section") next.sections[node.id] = node as any
  else next.articles[node.id] = node as any
  return next
}

/** Compact, agent-friendly view of an entry. */
export function describeEntry(e: ChangeEntry) {
  return {
    id: e.id,
    at: new Date(e.at).toISOString(),
    action: e.action,
    actor: e.actor,
    nodeId: e.nodeId,
    nodeKind: e.nodeKind,
    title: e.nodeTitle,
    path: e.path,
    locale: e.locale,
    summary: e.summary,
    reverted: !!e.reverted,
    author: e.authorName || e.authorEmail || (e.actor === "agent" ? "agent" : "unknown"),
    viaModel: e.viaModel,
    revertable: e.action === "create" ? !e.reverted : !!e.before?.nodes?.length && !e.reverted,
  }
}
